import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams, useLocation, useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getFormulario, updateFormulario, getFormularioRespuestas, deleteRespuesta, deleteFormulario, duplicateFormulario, getUsers, shareRespuesta } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useUnreadRespuestas } from "@/contexts/UnreadRespuestasContext";
import {
  ArrowLeft,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  FileQuestion,
  Loader2,
  GripVertical,
  Trash2,
  Plus,
  Save,
  Eye,
  Pencil,
  Copy,
  Share2,
  ArrowRightLeft,
  ClipboardPaste,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import EmojiPicker from "emoji-picker-react";
import { toast } from "sonner";
import { openAdjuntoEnNuevaPestana } from "@/lib/openAdjuntoEnNuevaPestana";
import { Textarea } from "@/components/ui/textarea";
import { parseFormQuestions, FORM_QUESTIONS_AI_PROMPT } from "@/lib/parseFormQuestions";

const EMOJI_PREDETERMINADO = "📄";

const BLOCK_TYPES_RAW = [
  { value: "titulo", label: "Título (H1–H6)" },
  { value: "parrafo", label: "Párrafo" },
  { value: "columnas", label: "Columnas (2–3)" },
  { value: "consentimiento_privacidad", label: "Consentimiento privacidad" },
  { value: "area_texto", label: "Área de texto" },
  { value: "imagen", label: "Imagen" },
  { value: "carousel", label: "Carousel" },
  { value: "input", label: "Campo de texto" },
  { value: "archivo", label: "Subida de archivos" },
  { value: "enlace", label: "Enlace" },
  { value: "enlaces_recursos", label: "Enlaces y recursos" },
  { value: "divisor", label: "Divisor" },
  { value: "opciones_checkbox", label: "Opciones Checkbox" },
  { value: "desplegable", label: "Desplegable" },
  { value: "paginacion", label: "Paginación" },
];
const BLOCK_TYPES = [...BLOCK_TYPES_RAW].sort((a, b) => a.label.localeCompare(b.label, "es"));
const INNER_BLOCK_TYPES = BLOCK_TYPES.filter((t) => t.value !== "paginacion" && t.value !== "columnas");

const DIVISOR_ESTILOS = [
  { value: "solid", label: "Línea (solid)" },
  { value: "dashed", label: "Discontinua (dashed)" },
  { value: "dotted", label: "Puntos (dotted)" },
];

function newBlockId() {
  return `blk_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function createBlock(tipo) {
  const id = newBlockId();
  switch (tipo) {
    case "titulo":
      return { id, tipo: "titulo", nivel: 1, texto: "" };
    case "parrafo":
      return { id, tipo: "parrafo", texto: "" };
    case "columnas":
      return { id, tipo: "columnas", columnas: 2, columnas_bloques: [[], []] };
    case "consentimiento_privacidad":
      return {
        id,
        tipo: "consentimiento_privacidad",
        titulo: "He leído y acepto la política de privacidad",
        texto_enlace: "política de privacidad",
        url_politica: "",
      };
    case "area_texto":
      return { id, tipo: "area_texto", titulo: "", placeholder: "" };
    case "imagen":
      return { id, tipo: "imagen", url: "" };
    case "carousel":
      return { id, tipo: "carousel", imagenes: [{ url: "" }] };
    case "input":
      return { id, tipo: "input", titulo: "", placeholder: "" };
    case "archivo":
      return { id, tipo: "archivo", titulo: "" };
    case "enlace":
      return { id, tipo: "enlace", url: "", texto: "" };
    case "enlaces_recursos":
      return { id, tipo: "enlaces_recursos", items: [{ tipo: "url", url: "", texto: "" }] };
    case "divisor":
      return { id, tipo: "divisor", altura_arriba: 16, altura_abajo: 16, estilo: "solid", grosor: 1 };
    case "opciones_checkbox":
      return { id, tipo: "opciones_checkbox", titulo: "", opciones: [{ label: "" }] };
    case "desplegable":
      return { id, tipo: "desplegable", titulo: "", opciones: [{ label: "" }] };
    case "paginacion":
      return { id, tipo: "paginacion" };
    default:
      return { id, tipo: "parrafo", texto: "" };
  }
}

function flattenBlocks(blocks) {
  const list = [];
  const walk = (arr) => {
    for (const b of arr || []) {
      if (!b || typeof b !== "object") continue;
      list.push(b);
      if (b.tipo === "columnas") {
        const cols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
        cols.forEach((col) => walk(Array.isArray(col) ? col : []));
      }
    }
  };
  walk(Array.isArray(blocks) ? blocks : []);
  return list;
}

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("Solo imágenes"));
      return;
    }
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });
}

function readFileAsDataUrlAny(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });
}

function isUrlLike(text) {
  if (!text || typeof text !== "string") return false;
  const t = text.trim();
  return /^https?:\/\/\S+/i.test(t) || /^www\.\S+/i.test(t);
}

function formatUrlForLink(text) {
  const t = (text || "").trim();
  if (/^https?:\/\/\S+/i.test(t)) return t;
  if (/^www\.\S+/i.test(t)) return `https://${t}`;
  return t;
}

function RespuestaConUrls({ value }) {
  if (value == null || value === "") return null;
  const str = String(value);
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = str.split(urlRegex);
  if (parts.length <= 1) {
    if (isUrlLike(str)) {
      return <a href={formatUrlForLink(str)} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 break-all">{str}</a>;
    }
    return <span>{str}</span>;
  }
  return (
    <span>
      {parts.map((part, i) => {
        if (isUrlLike(part)) {
          return <a key={i} href={formatUrlForLink(part)} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2 break-all">{part}</a>;
        }
        return <span key={i}>{part}</span>;
      })}
    </span>
  );
}

function getAdjuntoUrl(f) {
  if (!f || typeof f !== "object") return null;
  if (typeof f.url === "string" && f.url) return f.url;
  if (typeof f.data_url === "string" && f.data_url) return f.data_url;
  return null;
}

function parseRespuestaArchivos(value) {
  try {
    const parsed = typeof value === "string" ? JSON.parse(value) : value;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((f) => f && getAdjuntoUrl(f));
  } catch (_) {
    return [];
  }
}

function RespuestaArchivos({ value }) {
  const files = parseRespuestaArchivos(value);
  if (!files.length) return <span className="text-muted-foreground">Sin archivos</span>;
  return (
    <div className="flex flex-wrap gap-2">
      {files.map((f, i) => {
        const name = f.name || `archivo-${i + 1}`;
        const url = getAdjuntoUrl(f);
        const isImage = typeof f.type === "string" && f.type.startsWith("image/");
        return (
          <button
            key={`${name}-${i}`}
            type="button"
            onClick={() => openAdjuntoEnNuevaPestana(url)}
            className="inline-flex items-center gap-2 rounded-md border bg-muted/50 px-2.5 py-1.5 text-xs hover:bg-muted text-left cursor-pointer"
          >
            {isImage ? (
              <img src={url} alt={name} className="h-8 w-8 rounded object-cover border pointer-events-none" />
            ) : null}
            <span className="max-w-[240px] truncate">{name}</span>
          </button>
        );
      })}
    </div>
  );
}

function BlockPreview({ block, onCarouselImageClick }) {
  const b = block;
  if (!b || !b.tipo) return null;
  if (b.tipo === "titulo") {
    const n = Math.min(6, Math.max(1, b.nivel ?? 1));
    const Tag = `h${n}`;
    const cls = n === 6 ? "text-sm font-medium scroll-mt-6" : "font-semibold scroll-mt-6";
    return <Tag className={cls}>{b.texto || "\u00A0"}</Tag>;
  }
  if (b.tipo === "parrafo") {
    return <p className="text-muted-foreground whitespace-pre-wrap">{b.texto || "\u00A0"}</p>;
  }
  if (b.tipo === "columnas") {
    const colsCount = b.columnas === 3 ? 3 : 2;
    const cols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques.slice(0, colsCount) : [];
    while (cols.length < colsCount) cols.push([]);
    return (
      <div className="space-y-4">
        {cols.map((colBlocks, colIdx) => (
          <div key={colIdx} className="rounded-lg border border-dashed p-3 space-y-3 min-h-[96px]">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Columna {colIdx + 1}</p>
            {(Array.isArray(colBlocks) ? colBlocks : []).length === 0 ? (
              <p className="text-xs text-muted-foreground">Columna vacía</p>
            ) : (
              (Array.isArray(colBlocks) ? colBlocks : []).map((inner) => (
                <BlockPreview key={inner.id} block={inner} onCarouselImageClick={onCarouselImageClick} />
              ))
            )}
          </div>
        ))}
      </div>
    );
  }
  if (b.tipo === "area_texto") {
    return (
      <div className="space-y-1">
        {b.titulo && <Label className="font-medium">{b.titulo}{b.obligatorio && <span className="text-destructive"> *</span>}</Label>}
        <textarea
          readOnly
          disabled
          placeholder="El candidato escribirá aquí"
          className="flex min-h-[100px] w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm text-muted-foreground"
        />
      </div>
    );
  }
  if (b.tipo === "imagen" && b.url) {
    return (
      <button
        type="button"
        onClick={() => onCarouselImageClick?.([b.url], 0)}
        className="w-full rounded-lg border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring aspect-square"
      >
        <img src={b.url} alt="" className="w-full h-full object-cover cursor-pointer hover:opacity-90" />
      </button>
    );
  }
  if (b.tipo === "carousel") {
    const imgs = (b.imagenes || []).filter((img) => img?.url).map((img) => img.url);
    if (imgs.length === 0) return null;
    return (
      <div className="grid grid-cols-3 gap-2 py-2">
        {imgs.map((url, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onCarouselImageClick?.(imgs, i)}
            className="aspect-square rounded-lg border overflow-hidden focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <img src={url} alt="" className="w-full h-full object-cover cursor-pointer hover:opacity-90" />
          </button>
        ))}
      </div>
    );
  }
  if (b.tipo === "input") {
    return (
      <div className="space-y-1">
        {b.titulo && <Label className="text-sm font-medium">{b.titulo}{b.obligatorio && <span className="text-destructive"> *</span>}</Label>}
        <Input disabled placeholder={b.placeholder || ""} className="bg-muted/50" />
      </div>
    );
  }
  if (b.tipo === "archivo") {
    return (
      <div className="space-y-1">
        {b.titulo && <Label className="text-sm font-medium">{b.titulo}{b.obligatorio && <span className="text-destructive"> *</span>}</Label>}
        <div className="rounded-md border border-dashed bg-muted/40 px-3 py-3 text-sm text-muted-foreground">
          El candidato podrá adjuntar imágenes, PDF u otros archivos.
        </div>
      </div>
    );
  }
  if (b.tipo === "enlace") {
    const url = b.url || "#";
    const text = b.texto || url;
    return (
      <p>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">{text}</a>
      </p>
    );
  }
  if (b.tipo === "enlaces_recursos") {
    const items = b.items || [];
    return (
      <div className="space-y-1">
        <p className="text-sm font-medium">Enlaces y recursos</p>
        <div className="flex flex-wrap gap-2">
          {items.map((item, i) => {
            if (item.tipo === "url" && (item.url || item.texto)) {
              return <a key={i} href={item.url || "#"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm">{item.texto || item.url}</a>;
            }
            if (item.tipo === "archivo" && (item.url || item.nombre_archivo)) {
              const label = item.nombre_archivo || "Descargar";
              if (item.url) {
                return <a key={i} href={item.url} download={item.nombre_archivo} className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm hover:bg-muted">{label}</a>;
              }
              return <span key={i} className="inline-flex items-center rounded-md border bg-muted/50 px-3 py-1.5 text-sm">{label}</span>;
            }
            return null;
          })}
        </div>
      </div>
    );
  }
  if (b.tipo === "divisor") {
    const arriba = Math.max(0, parseInt(b.altura_arriba, 10) || 16);
    const abajo = Math.max(0, parseInt(b.altura_abajo, 10) || 16);
    const grosor = Math.max(0, Math.min(4, parseInt(b.grosor, 10) ?? 1));
    const estilo = b.estilo === "dashed" ? "dashed" : b.estilo === "dotted" ? "dotted" : "solid";
    return (
      <div
        className="border-t border-gray-300"
        style={{
          marginTop: `${arriba}px`,
          marginBottom: `${abajo}px`,
          borderTopWidth: grosor === 0 ? 0 : `${grosor}px`,
          borderTopStyle: grosor === 0 ? "none" : estilo,
        }}
        aria-hidden
      />
    );
  }
  if (b.tipo === "opciones_checkbox") {
    const opciones = (b.opciones || []).filter((o) => (o?.label || "").trim());
    return (
      <div className="space-y-2">
        {b.titulo && <Label className="text-sm font-medium">{b.titulo}{b.obligatorio && <span className="text-destructive"> *</span>}</Label>}
        <div className="flex flex-col gap-2">
          {opciones.length ? opciones.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="checkbox" disabled className="rounded border-input" />
              <span className="text-sm">{o.label || "\u00A0"}</span>
            </div>
          )) : <span className="text-sm text-muted-foreground">Añade opciones en el editor</span>}
        </div>
      </div>
    );
  }
  if (b.tipo === "desplegable") {
    const opciones = (b.opciones || []).filter((o) => (o?.label || "").trim());
    return (
      <div className="space-y-2">
        {b.titulo && <Label className="text-sm font-medium">{b.titulo}{b.obligatorio && <span className="text-destructive"> *</span>}</Label>}
        <select disabled className="flex h-9 w-full rounded-md border border-input bg-muted/50 px-3 py-2 text-sm">
          <option value="">{opciones.length ? "Selecciona..." : "Añade opciones en el editor"}</option>
          {opciones.map((o, i) => <option key={i} value={o.label}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  if (b.tipo === "consentimiento_privacidad") {
    const texto = (b.titulo || "").trim() || "He leído y acepto la";
    const textoEnlace = (b.texto_enlace || "").trim() || "política de privacidad";
    const href = (b.url_politica || "").trim();
    return (
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" disabled className="mt-0.5 rounded border-input" />
        <span>
          {texto}{" "}
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
              {textoEnlace}
            </a>
          ) : (
            <span className="underline underline-offset-2">{textoEnlace}</span>
          )}
        </span>
      </label>
    );
  }
  if (b.tipo === "paginacion") {
    return (
      <div className="py-4 text-center text-sm text-muted-foreground border border-dashed rounded-lg">
        ——— Siguiente página ———
      </div>
    );
  }
  return null;
}

function BlockRow({
  block,
  index,
  allBlocks,
  onUpdate,
  onRemove,
  onDuplicate,
  onMove,
  onDragStart,
  onDragOver,
  onDrop,
  isDragging,
  addWidgetMenuOptions = null,
  onAddWidgetAfter = null,
}) {
  const [divisorOpcionesOpen, setDivisorOpcionesOpen] = useState(false);
  const b = block;

  const update = (field, value) => {
    onUpdate(index, { ...b, [field]: value });
  };

  const updateNested = (field, indexInner, value) => {
    const arr = Array.isArray(b[field]) ? [...b[field]] : [];
    arr[indexInner] = value;
    onUpdate(index, { ...b, [field]: arr });
  };

  const addItem = (field, defaultItem) => {
    const arr = Array.isArray(b[field]) ? [...b[field]] : [];
    onUpdate(index, { ...b, [field]: [...arr, defaultItem] });
  };

  const removeItem = (field, indexInner) => {
    const arr = (b[field] || []).filter((_, i) => i !== indexInner);
    onUpdate(index, { ...b, [field]: arr });
  };

  const blockLabel = BLOCK_TYPES.find((t) => t.value === b.tipo)?.label ?? b.tipo;
  const positionNumber = index + 1;

  const showAddAfter =
    typeof onAddWidgetAfter === "function" && Array.isArray(addWidgetMenuOptions) && addWidgetMenuOptions.length > 0;

  return (
    <div
      className={`group/block-row relative rounded-lg border border-gray-300 bg-gray-50 ${isDragging ? "opacity-50" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-block-index={index}
    >
      <div className="flex gap-2 p-3">
      <div
        className="cursor-grab active:cursor-grabbing shrink-0 self-center text-muted-foreground hover:text-foreground touch-none"
        aria-label="Reordenar"
      >
        <GripVertical className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1 min-w-0 space-y-2">
        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide m-0">
          {positionNumber}. {blockLabel}
        </h5>
        {b.tipo === "titulo" && (
          <>
            <div className="flex gap-2 items-center">
              <Label className="shrink-0">Nivel</Label>
              <Select
                value={String(b.nivel ?? 1)}
                onValueChange={(v) => update("nivel", parseInt(v, 10))}
              >
                <SelectTrigger className="w-20 bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <SelectItem key={n} value={String(n)}>H{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input
              className="bg-white border-gray-300"
              placeholder="Texto del título"
              value={b.texto ?? ""}
              onChange={(e) => update("texto", e.target.value)}
            />
          </>
        )}
        {b.tipo === "parrafo" && (
          <textarea
            className="flex min-h-[80px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            placeholder="Texto del párrafo"
            value={b.texto ?? ""}
            onChange={(e) => update("texto", e.target.value)}
          />
        )}
        {b.tipo === "area_texto" && (
          <>
            <Input
              className="bg-white border-gray-300"
              placeholder="Título del campo (ej: Comentarios)"
              value={b.titulo ?? ""}
              onChange={(e) => update("titulo", e.target.value)}
            />
            <Input
              className="bg-white border-gray-300"
              placeholder="Placeholder del textarea (opcional)"
              value={b.placeholder ?? b.texto ?? ""}
              onChange={(e) => update("placeholder", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">El candidato verá un área de texto para escribir aquí.</p>
          </>
        )}
        {b.tipo === "imagen" && (
          <div className="space-y-2">
            <Label>Imagen (220px alto)</Label>
            {b.url ? (
              <div className="relative rounded-lg border overflow-hidden">
                <img src={b.url} alt="" className="w-full h-[220px] object-cover" />
                <div className="absolute bottom-2 right-2 flex gap-2">
                  <Button type="button" variant="secondary" size="sm" className="shadow" onClick={() => document.getElementById(`imagen-block-${b.id}`)?.click()}>
                    Cambiar imagen
                  </Button>
                  <Button type="button" variant="secondary" size="sm" className="shadow" onClick={() => update("url", "")}>Quitar</Button>
                </div>
              </div>
            ) : (
              <div
                className="border border-dashed rounded-lg h-[220px] flex items-center justify-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/30"
                onClick={() => document.getElementById(`imagen-block-${b.id}`)?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-muted/50"); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove("bg-muted/50"); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove("bg-muted/50");
                  const file = (e.dataTransfer.files || [])[0];
                  if (file?.type.startsWith("image/")) {
                    readFileAsDataUrl(file).then((url) => update("url", url)).catch(() => {});
                  }
                }}
              >
                Arrastra una imagen o haz clic
              </div>
            )}
            <input
              id={`imagen-block-${b.id}`}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = (e.target.files || [])[0];
                if (file) readFileAsDataUrl(file).then((url) => update("url", url)).catch(() => {});
                e.target.value = "";
              }}
            />
          </div>
        )}
        {b.tipo === "carousel" && (
          <div className="space-y-2">
            <Label>Imágenes (arrastra o selecciona)</Label>
            <div className="flex flex-wrap gap-2">
              {(b.imagenes || []).map((img, i) => (
                <div key={i} className="relative group">
                  {img?.url ? (
                    <img src={img.url} alt="" className="h-20 w-20 object-cover rounded border" />
                  ) : (
                    <div className="h-20 w-20 rounded border border-dashed bg-muted/30 flex items-center justify-center text-muted-foreground text-xs">?</div>
                  )}
                  <Button type="button" variant="destructive" size="icon" className="absolute -top-2 -right-2 h-6 w-6 opacity-90 group-hover:opacity-100" onClick={() => removeItem("imagenes", i)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
            <div
              className="border border-dashed border-gray-300 rounded-lg p-4 text-center text-sm text-muted-foreground bg-white hover:bg-gray-50 cursor-pointer"
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-gray-100"); }}
              onDragLeave={(e) => { e.currentTarget.classList.remove("bg-gray-100"); }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.classList.remove("bg-gray-100");
                const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith("image/"));
                Promise.all(files.map((file) => readFileAsDataUrl(file)))
                  .then((urls) => {
                    const newItems = urls.map((url) => ({ url }));
                    onUpdate(index, { ...b, imagenes: [...(b.imagenes || []), ...newItems] });
                  })
                  .catch(() => {});
              }}
              onClick={() => document.getElementById(`carousel-file-${b.id}`)?.click()}
            >
              Arrastra imágenes aquí o haz clic para elegir
            </div>
            <input
              id={`carousel-file-${b.id}`}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                Promise.all(files.map((file) => readFileAsDataUrl(file)))
                  .then((urls) => {
                    const newItems = urls.map((url) => ({ url }));
                    onUpdate(index, { ...b, imagenes: [...(b.imagenes || []), ...newItems] });
                  })
                  .catch(() => {});
                e.target.value = "";
              }}
            />
          </div>
        )}
        {b.tipo === "input" && (
          <>
            <Input
              className="bg-white border-gray-300"
              placeholder="Etiqueta del campo"
              value={b.titulo ?? ""}
              onChange={(e) => update("titulo", e.target.value)}
            />
            <Input
              className="bg-white border-gray-300"
              placeholder="Placeholder (opcional)"
              value={b.placeholder ?? ""}
              onChange={(e) => update("placeholder", e.target.value)}
            />
          </>
        )}
        {b.tipo === "archivo" && (
          <>
            <Input
              className="bg-white border-gray-300"
              placeholder="Título del campo (ej: Adjunta tu portfolio)"
              value={b.titulo ?? ""}
              onChange={(e) => update("titulo", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              El candidato podrá seleccionar varios archivos (imágenes, PDF, etc.).
            </p>
          </>
        )}
        {b.tipo === "enlace" && (
          <>
            <Input
              className="bg-white border-gray-300"
              placeholder="URL"
              value={b.url ?? ""}
              onChange={(e) => update("url", e.target.value)}
            />
            <Input
              className="bg-white border-gray-300"
              placeholder="Texto del enlace"
              value={b.texto ?? ""}
              onChange={(e) => update("texto", e.target.value)}
            />
          </>
        )}
        {b.tipo === "divisor" && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setDivisorOpcionesOpen((v) => !v)}
                className="text-sm font-medium hover:underline text-left"
              >
                Divisor
              </button>
              <Button type="button" variant="outline" size="sm" onClick={() => setDivisorOpcionesOpen((v) => !v)}>
                Opciones
              </Button>
            </div>
            {divisorOpcionesOpen && (
              <div className="space-y-3 pt-3 border-t border-gray-300">
                <Label className="text-xs text-muted-foreground">Espaciado (px)</Label>
                <div className="flex gap-4 items-center flex-wrap">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">Arriba</Label>
                    <Input
                      type="number"
                      min={0}
                      max={200}
                      value={b.altura_arriba ?? 16}
                      onChange={(e) => update("altura_arriba", e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
                      className="w-20 bg-white border-gray-300"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs text-muted-foreground shrink-0">Abajo</Label>
                    <Input
                      type="number"
                      min={0}
                      max={200}
                      value={b.altura_abajo ?? 16}
                      onChange={(e) => update("altura_abajo", e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0)}
                      className="w-20 bg-white border-gray-300"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground shrink-0">Estilo</Label>
                  <Select value={b.estilo ?? "solid"} onValueChange={(v) => update("estilo", v)}>
                    <SelectTrigger className="w-[180px] bg-white border-gray-300">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DIVISOR_ESTILOS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground shrink-0">Grosor (px)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={4}
                    value={b.grosor ?? 1}
                    onChange={(e) => update("grosor", e.target.value === "" ? 0 : Math.min(4, Math.max(0, parseInt(e.target.value, 10) ?? 0)))}
                    className="w-20 bg-white border-gray-300"
                  />
                  <span className="text-xs text-muted-foreground">0 = solo separador (sin línea)</span>
                </div>
              </div>
            )}
          </div>
        )}
        {b.tipo === "enlaces_recursos" && (
          <div className="space-y-2">
            <Label>Items</Label>
            {(b.items || []).map((item, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center rounded border border-gray-300 bg-white p-2 w-full">
                <Select
                  value={item.tipo || "url"}
                  onValueChange={(v) => updateNested("items", i, { ...item, tipo: v })}
                >
                  <SelectTrigger className="w-28 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="url">URL</SelectItem>
                    <SelectItem value="archivo">Archivo</SelectItem>
                  </SelectContent>
                </Select>
                {(item.tipo || "url") === "url" && (
                  <>
                    <Input
                      className="flex-1 min-w-[120px] bg-white border-gray-300"
                      placeholder="URL"
                      value={item.url ?? ""}
                      onChange={(e) => updateNested("items", i, { ...item, url: e.target.value })}
                    />
                    <Input
                      className="flex-1 min-w-[120px] bg-white border-gray-300"
                      placeholder="Texto"
                      value={item.texto ?? ""}
                      onChange={(e) => updateNested("items", i, { ...item, texto: e.target.value })}
                    />
                  </>
                )}
                {(item.tipo || "url") === "archivo" && (
                  <>
                    <input
                      id={`enlaces-recursos-archivo-${b.id}-${i}`}
                      type="file"
                      className="hidden"
                      onChange={(e) => {
                        const file = (e.target.files || [])[0];
                        if (file) {
                          readFileAsDataUrlAny(file).then((url) => {
                            updateNested("items", i, { ...item, url, nombre_archivo: file.name });
                          }).catch(() => {});
                        }
                        e.target.value = "";
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById(`enlaces-recursos-archivo-${b.id}-${i}`)?.click()}
                    >
                      {item.nombre_archivo || "Seleccionar archivo"}
                    </Button>
                    {item.url && (
                      <span className="text-xs text-muted-foreground truncate max-w-[140px]" title={item.nombre_archivo}>{item.nombre_archivo}</span>
                    )}
                  </>
                )}
                <div className="ml-auto shrink-0">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removeItem("items", i)} aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addItem("items", { tipo: "url", url: "", texto: "" })}
            >
              <Plus className="w-4 h-4 mr-1" /> Añadir item
            </Button>
          </div>
        )}
        {(b.tipo === "opciones_checkbox" || b.tipo === "desplegable") && (
          <div className="space-y-2">
            <Input
              className="bg-white border-gray-300"
              placeholder="Título del campo (opcional)"
              value={b.titulo ?? ""}
              onChange={(e) => update("titulo", e.target.value)}
            />
            <Label className="text-xs text-muted-foreground">Opciones</Label>
            {(b.opciones || []).map((opt, i) => (
              <div key={i} className="flex gap-2 items-center">
                <Input
                  className="flex-1 bg-white border-gray-300"
                  placeholder={`Opción ${i + 1}`}
                  value={opt?.label ?? ""}
                  onChange={(e) => updateNested("opciones", i, { ...opt, label: e.target.value })}
                />
                <Button type="button" variant="ghost" size="icon" onClick={() => removeItem("opciones", i)} aria-label="Eliminar opción">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addItem("opciones", { label: "" })}
            >
              <Plus className="w-4 h-4 mr-1" /> Añadir opción
            </Button>
          </div>
        )}
        {b.tipo === "consentimiento_privacidad" && (
          <div className="space-y-2">
            <Input
              className="bg-white border-gray-300"
              placeholder="Texto previo al enlace"
              value={b.titulo ?? ""}
              onChange={(e) => update("titulo", e.target.value)}
            />
            <Input
              className="bg-white border-gray-300"
              placeholder="Texto del enlace"
              value={b.texto_enlace ?? ""}
              onChange={(e) => update("texto_enlace", e.target.value)}
            />
            <Input
              className="bg-white border-gray-300"
              placeholder="URL de la política (https://...)"
              value={b.url_politica ?? ""}
              onChange={(e) => update("url_politica", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Este checkbox será obligatorio en el formulario público antes de enviar.
            </p>
          </div>
        )}
        {["input", "area_texto", "opciones_checkbox", "desplegable", "archivo"].includes(b.tipo) && (
          <label className="flex items-center gap-2 text-sm cursor-pointer w-fit">
            <input
              type="checkbox"
              checked={Boolean(b.obligatorio)}
              onChange={(e) => update("obligatorio", e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Pregunta obligatoria
          </label>
        )}
        {b.tipo === "columnas" && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Label className="shrink-0">Número de columnas</Label>
              <Select
                value={String(b.columnas === 3 ? 3 : 2)}
                onValueChange={(v) => {
                  const count = v === "3" ? 3 : 2;
                  const prevCols = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
                  const nextCols = Array.from({ length: count }, (_, i) => {
                    const col = prevCols[i];
                    return Array.isArray(col) ? col : [];
                  });
                  onUpdate(index, { ...b, columnas: count, columnas_bloques: nextCols });
                }}
              >
                <SelectTrigger className="w-28 bg-white border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="2">2</SelectItem>
                  <SelectItem value="3">3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              {(() => {
                const colsCount = b.columnas === 3 ? 3 : 2;
                const columnasBloquesRoot = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
                const moveInnerAcrossColumns = (fromColIdx, fromInnerIdx, toColIdx) => {
                  if (toColIdx < 0 || toColIdx >= colsCount || fromColIdx === toColIdx) return;
                  const base = Array.from({ length: colsCount }, (_, i) => {
                    const col = columnasBloquesRoot[i];
                    return Array.isArray(col) ? [...col] : [];
                  });
                  const source = base[fromColIdx] || [];
                  const target = base[toColIdx] || [];
                  const moving = source[fromInnerIdx];
                  if (!moving) return;
                  source.splice(fromInnerIdx, 1);
                  target.push(moving);
                  base[fromColIdx] = source;
                  base[toColIdx] = target;
                  onUpdate(index, { ...b, columnas_bloques: base });
                };

                return Array.from({ length: colsCount }).map((_, colIdx) => {
                  const columnasBloques = Array.isArray(b.columnas_bloques) ? b.columnas_bloques : [];
                  const innerBlocks = Array.isArray(columnasBloques[colIdx]) ? columnasBloques[colIdx] : [];
                  const setInnerBlocks = (nextInner) => {
                    const base = Array.from({ length: colsCount }, (_, i) => {
                      const col = columnasBloques[i];
                      return Array.isArray(col) ? [...col] : [];
                    });
                    base[colIdx] = nextInner;
                    onUpdate(index, { ...b, columnas_bloques: base });
                  };
                  const updateInnerAt = (innerIndex, newInnerBlock) => {
                    const next = [...innerBlocks];
                    next[innerIndex] = newInnerBlock;
                    setInnerBlocks(next);
                  };
                  const removeInnerAt = (innerIndex) => setInnerBlocks(innerBlocks.filter((_, i) => i !== innerIndex));
                  const duplicateInnerAt = (innerIndex) => {
                    const src = innerBlocks[innerIndex];
                    if (!src) return;
                    const copy = { ...src, id: newBlockId() };
                    if (copy.items) copy.items = copy.items.map((it) => ({ ...it }));
                    if (copy.imagenes) copy.imagenes = copy.imagenes.map((img) => ({ ...img }));
                    const next = [...innerBlocks];
                    next.splice(innerIndex + 1, 0, copy);
                    setInnerBlocks(next);
                  };

                  return (
                    <div key={colIdx} className="rounded-lg border border-dashed p-3 space-y-2 bg-white/50">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Columna {colIdx + 1}</p>
                      {innerBlocks.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Sin widgets.</p>
                      ) : (
                        <div className="space-y-2">
                          {innerBlocks.map((innerBlock, innerIndex) => (
                            <div key={innerBlock.id} className="space-y-1">
                              <BlockRow
                                block={innerBlock}
                                index={innerIndex}
                                allBlocks={innerBlocks}
                                onUpdate={updateInnerAt}
                                onRemove={removeInnerAt}
                                onDuplicate={duplicateInnerAt}
                                onMove={undefined}
                                onDragStart={() => {}}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={() => {}}
                                isDragging={false}
                                addWidgetMenuOptions={INNER_BLOCK_TYPES}
                                onAddWidgetAfter={(tipo) => {
                                  const next = [...innerBlocks];
                                  next.splice(innerIndex + 1, 0, createBlock(tipo));
                                  setInnerBlocks(next);
                                }}
                              />
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => moveInnerAcrossColumns(colIdx, innerIndex, colIdx - 1)}
                                  disabled={colIdx === 0}
                                  title="Mover a columna anterior"
                                  aria-label="Mover a columna anterior"
                                >
                                  <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => moveInnerAcrossColumns(colIdx, innerIndex, colIdx + 1)}
                                  disabled={colIdx >= colsCount - 1}
                                  title="Mover a columna siguiente"
                                  aria-label="Mover a columna siguiente"
                                >
                                  <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button type="button" variant="outline" size="sm" className="w-full gap-2">
                            <Plus className="w-4 h-4" strokeWidth={1.5} />
                            Añadir widget
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {INNER_BLOCK_TYPES.map((opt) => (
                            <DropdownMenuItem
                              key={opt.value}
                              onSelect={() => setInnerBlocks([...innerBlocks, createBlock(opt.value)])}
                            >
                              {opt.label}
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        )}
        {b.tipo === "paginacion" && (
          <p className="text-sm text-muted-foreground">
            Los bloques que coloques debajo de este irán en la siguiente página. En la vista pública habrá botones «Anterior» / «Siguiente» y en la última página «Enviar».
          </p>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {onMove && Array.isArray(allBlocks) && allBlocks.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="ghost" size="icon" title="Mover a posición" aria-label="Mover a posición">
                <ArrowRightLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[280px] overflow-y-auto">
              {allBlocks.map((blk, i) => {
                const num = i + 1;
                const label = BLOCK_TYPES.find((t) => t.value === blk?.tipo)?.label ?? blk?.tipo ?? "";
                const isCurrent = i === index;
                return (
                  <DropdownMenuItem
                    key={blk?.id ?? i}
                    onSelect={() => !isCurrent && onMove(index, num)}
                    disabled={isCurrent}
                    className={isCurrent ? "opacity-60" : ""}
                  >
                    {num}. {label}
                    {isCurrent && " (actual)"}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button type="button" variant="ghost" size="icon" onClick={() => onDuplicate(index)} aria-label="Duplicar bloque">
              <Copy className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Duplicar</TooltipContent>
        </Tooltip>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={() => onRemove(index)}
          aria-label="Eliminar bloque"
        >
          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
        </Button>
      </div>
      </div>
      {showAddAfter ? (
        <div className="flex justify-center border-t border-transparent px-3 py-1.5 transition-colors group-hover/block-row:border-gray-200">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 gap-1 text-xs shadow-sm bg-background opacity-0 pointer-events-none transition-opacity group-hover/block-row:opacity-100 group-hover/block-row:pointer-events-auto"
                aria-label="Añadir widget siguiente"
              >
                <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
                Añadir siguiente
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="center" className="max-h-[280px] overflow-y-auto">
              {addWidgetMenuOptions.map((opt) => (
                <DropdownMenuItem key={opt.value} onSelect={() => onAddWidgetAfter(opt.value)}>
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </div>
  );
}

export default function FormularioDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const outletContext = useOutletContext();
  const setFormularioPreview = outletContext?.setFormularioPreview;
  const addFormularioToSidebarList = outletContext?.addFormularioToSidebarList;
  const removeFormularioFromSidebarList = outletContext?.removeFormularioFromSidebarList;
  const isEmbeddedInFormularios = (location.pathname || "").startsWith("/formularios/");
  const { user, authLoading } = useAuth();
  const [formulario, setFormulario] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [slug, setSlug] = useState("");
  const [notificacionEmails, setNotificacionEmails] = useState([]);
  const [notificacionIdentificadorBloqueId, setNotificacionIdentificadorBloqueId] = useState("");
  const [imagenCabecera, setImagenCabecera] = useState("");
  const [imagenFondo, setImagenFondo] = useState("");
  const [emoji, setEmoji] = useState("");
  const [bloques, setBloques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [respuestas, setRespuestas] = useState([]);
  const [respuestasLoading, setRespuestasLoading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [activeTab, setActiveTab] = useState("formulario");
  const [toolbarExiting, setToolbarExiting] = useState(false);
  const [modoVisualizar, setModoVisualizar] = useState(false);
  const [lightboxUrls, setLightboxUrls] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [expandedRespuestaId, setExpandedRespuestaId] = useState(null);
  const [showDeleteFormularioConfirm, setShowDeleteFormularioConfirm] = useState(false);
  const [deletingFormulario, setDeletingFormulario] = useState(false);
  const [usuariosPlataforma, setUsuariosPlataforma] = useState([]);
  const [sharingRespuestaId, setSharingRespuestaId] = useState(null);
  const fondoInputRef = useRef(null);
  const cabeceraInputRef = useRef(null);
  const { unreadByFormulario, setUnreadForFormulario, setRespuestaRead, isRespuestaRead, readRespuestaIdsByFormulario } = useUnreadRespuestas();
  const readRespuestaIdsByFormularioRef = useRef(readRespuestaIdsByFormulario);
  readRespuestaIdsByFormularioRef.current = readRespuestaIdsByFormulario;

  const blockIdToTitle = useMemo(() => {
    const map = {};
    flattenBlocks(bloques || []).forEach((b) => {
      if (!b || !b.id) return;
      if (b.tipo === "input" || b.tipo === "area_texto" || b.tipo === "opciones_checkbox" || b.tipo === "desplegable" || b.tipo === "archivo" || b.tipo === "consentimiento_privacidad")
        map[b.id] = (b.titulo || "").trim() || (b.placeholder || "").trim() || BLOCK_TYPES.find((t) => t.value === b.tipo)?.label || b.tipo;
      else map[b.id] = BLOCK_TYPES.find((t) => t.value === b.tipo)?.label || b.tipo;
    });
    return map;
  }, [bloques]);

  const blockIdToType = useMemo(() => {
    const map = {};
    flattenBlocks(bloques || []).forEach((b) => {
      if (!b || !b.id) return;
      map[b.id] = b.tipo || "";
    });
    return map;
  }, [bloques]);

  const identificadorOptions = useMemo(
    () => flattenBlocks(bloques || [])
      .filter((b) => b && b.id && ["input", "area_texto", "opciones_checkbox", "desplegable", "archivo"].includes(b.tipo))
      .map((b) => ({
        id: b.id,
        label: (b.titulo || "").trim() || (b.placeholder || "").trim() || BLOCK_TYPES.find((t) => t.value === b.tipo)?.label || b.tipo,
      })),
    [bloques]
  );

  function formatRespuestaFecha(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const h = d.getHours();
    const m = d.getMinutes();
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}h | ${day}.${month}.${year}`;
  }

  function getNombreFromRespuestas(respuestas) {
    if (!respuestas || typeof respuestas !== "object") return "Sin nombre";
    const entries = Object.entries(respuestas);
    if (entries.length === 0) return "Sin nombre";
    const nameLike = /nombre|llamas|nombre y apellidos|cómo te llamas/i;
    for (const [blockId, value] of entries) {
      const title = blockIdToTitle[blockId] || "";
      if (nameLike.test(title) && value != null && String(value).trim()) return String(value).trim();
    }
    const first = entries[0];
    return first[1] != null && String(first[1]).trim() ? String(first[1]).trim() : "Sin nombre";
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/login", { replace: true });
      return;
    }
    if (user.permissions?.formularios === false) {
      if (user.permissions?.candidatos !== false) navigate("/candidatos", { replace: true });
      else if (user.permissions?.revisiones !== false) navigate("/revisiones", { replace: true });
      else if (user.permissions?.onboarding !== false) navigate("/onboarding", { replace: true });
      else if (user.permissions?.proveedores !== false) navigate("/proveedores", { replace: true });
      else if (user.permissions?.archivos !== false) navigate("/brutalfiles", { replace: true });
      else if (user.permissions?.correos !== false) navigate("/correos", { replace: true });
      else if (user.permissions?.metricas) navigate("/metricas", { replace: true });
      else navigate("/", { replace: true });
      return;
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!id || user?.permissions?.formularios === false) return;
    setLoading(true);
    getFormulario(id)
      .then((data) => {
        setFormulario(data);
        setTitulo(data.titulo ?? "");
        setSlug(data.slug ?? "");
        setImagenCabecera(data.imagen_cabecera ?? "");
        setImagenFondo(data.imagen_fondo ?? "");
        setEmoji(data.emoji ?? "");
        setBloques(Array.isArray(data.bloques) ? data.bloques : []);
        setNotificacionEmails(Array.isArray(data.notificacion_emails) ? data.notificacion_emails : []);
        setNotificacionIdentificadorBloqueId((data.notificacion_identificador_bloque_id || "").trim());
      })
      .catch(() => {
        toast.error("Error al cargar el formulario");
        navigate("/formularios");
      })
      .finally(() => setLoading(false));
  }, [id, user?.permissions?.formularios, navigate]);

  useEffect(() => {
    if (!notificacionIdentificadorBloqueId) return;
    const exists = flattenBlocks(bloques || []).some((b) => b?.id === notificacionIdentificadorBloqueId);
    if (!exists) setNotificacionIdentificadorBloqueId("");
  }, [bloques, notificacionIdentificadorBloqueId]);

  const loadRespuestas = useCallback(() => {
    if (!id) return;
    setRespuestasLoading(true);
    getFormularioRespuestas(id)
      .then((data) => {
        const list = data.respuestas || [];
        setRespuestas(list);
        const readByFormulario = readRespuestaIdsByFormularioRef.current[id] || {};
        const unreadCount = list.filter((r) => !readByFormulario[r.id]).length;
        setUnreadForFormulario(id, unreadCount);
      })
      .catch(() => toast.error("Error al cargar respuestas"))
      .finally(() => setRespuestasLoading(false));
  }, [id, setUnreadForFormulario]);

  const handleDuplicateFormulario = useCallback(async () => {
    if (!id || !formulario) return;
    const confirmed = window.confirm("¿Quieres duplicar este formulario? Se creará una copia con el mismo contenido.");
    if (!confirmed) return;
    try {
      setDuplicating(true);
      const data = await duplicateFormulario(id);
      const newId = data.id;
      toast.success("Formulario duplicado");
      if (newId) {
        addFormularioToSidebarList?.({
          id: newId,
          titulo: data.titulo,
          slug: data.slug,
          fecha_creacion: new Date().toISOString(),
          emoji: formulario?.emoji,
        });
        navigate(`/formularios/${newId}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al duplicar el formulario");
    } finally {
      setDuplicating(false);
    }
  }, [id, formulario, navigate, addFormularioToSidebarList]);

  const handleDeleteRespuesta = useCallback(
    async (e, respuestaId) => {
      e.stopPropagation();
      if (!id) return;
      if (!window.confirm("¿Eliminar esta respuesta? Esta acción no se puede deshacer.")) return;
      try {
        await deleteRespuesta(id, respuestaId);
        setRespuestas((prev) => prev.filter((r) => r.id !== respuestaId));
        toast.success("Respuesta eliminada");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al eliminar");
      }
    },
    [id]
  );

  const handleShareRespuesta = useCallback(
    async (e, respuestaId) => {
      e.stopPropagation();
      if (!id || sharingRespuestaId) return;
      setSharingRespuestaId(respuestaId);
      try {
        const data = await shareRespuesta(id, respuestaId);
        const text = `Enlace a la respuesta: ${data.share_url} Contraseña: ${data.password}`;
        await navigator.clipboard.writeText(text);
        toast.success("Enlace de respuesta copiado");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Error al compartir respuesta");
      } finally {
        setSharingRespuestaId(null);
      }
    },
    [id, sharingRespuestaId]
  );

  // Cargar respuestas al montar para que el badge aparezca en sidebar y en la tab sin entrar
  useEffect(() => {
    if (id) loadRespuestas();
  }, [id, loadRespuestas]);

  // Cargar lista de usuarios de la plataforma para el selector de notificaciones
  useEffect(() => {
    getUsers()
      .then((data) => {
        const list = Array.isArray(data.users) ? data.users : [];
        const sorted = [...list].sort((a, b) =>
          String(a.nombre || a.email || "").trim().localeCompare(String(b.nombre || b.email || "").trim(), undefined, { sensitivity: "base" })
        );
        setUsuariosPlataforma(sorted);
      })
      .catch(() => setUsuariosPlataforma([]));
  }, []);

  // Al entrar en tab Respuestas: animación de salida de la barra de herramientas; al volver a Formulario: entrada
  useEffect(() => {
    if (activeTab === "respuestas") setToolbarExiting(true);
    else setToolbarExiting(false);
  }, [activeTab]);
  useEffect(() => {
    if (!toolbarExiting) return;
    const t = setTimeout(() => setToolbarExiting(false), 200);
    return () => clearTimeout(t);
  }, [toolbarExiting]);

  // Sincronizar preview con el sidebar en vivo (titulo + icono al editar).
  useEffect(() => {
    if (!id || !setFormularioPreview) return;
    setFormularioPreview(id, { titulo, emoji, imagen_cabecera: imagenCabecera });
    return () => setFormularioPreview(id, null);
  }, [id, titulo, emoji, imagenCabecera, setFormularioPreview]);

  const handleUpdateBlock = (index, newBlock) => {
    setBloques((prev) => {
      const next = [...prev];
      next[index] = newBlock;
      return next;
    });
  };

  const handleRemoveBlock = (index) => {
    setBloques((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDuplicateBlock = (index) => {
    const block = bloques[index];
    if (!block) return;
    const copy = { ...block, id: newBlockId() };
    if (copy.items) copy.items = copy.items.map((it) => ({ ...it }));
    if (copy.imagenes) copy.imagenes = copy.imagenes.map((img) => ({ ...img }));
    setBloques((prev) => {
      const next = [...prev];
      next.splice(index + 1, 0, copy);
      return next;
    });
  };

  const handleAddBlock = (tipo) => {
    setBloques((prev) => [...prev, createBlock(tipo)]);
  };

  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState("");
  const pasteResult = useMemo(() => parseFormQuestions(pasteText), [pasteText]);

  const handleImportPaste = () => {
    if (!pasteResult.bloques.length) return;
    setBloques((prev) => [...prev, ...pasteResult.bloques]);
    toast.success(`${pasteResult.bloques.length} bloques añadidos`);
    setPasteText("");
    setPasteOpen(false);
  };

  const handleAddBlockAfter = useCallback((afterIndex, tipo) => {
    setBloques((prev) => {
      const next = [...prev];
      const at = Math.min(Math.max(0, afterIndex + 1), next.length);
      next.splice(at, 0, createBlock(tipo));
      return next;
    });
  }, []);

  const handleDragStart = (e) => {
    const idx = e.currentTarget.getAttribute("data-block-index");
    if (idx != null) setDragIndex(parseInt(idx, 10));
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", idx ?? "");
    e.dataTransfer.setData("block-index", idx ?? "");
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const fromStr = e.dataTransfer.getData("block-index") || e.dataTransfer.getData("text/plain");
    const from = fromStr !== "" ? parseInt(fromStr, 10) : null;
    const toStr = e.currentTarget.getAttribute("data-block-index");
    const to = toStr != null ? parseInt(toStr, 10) : null;
    setDragIndex(null);
    if (from == null || to == null || from === to) return;
    setBloques((prev) => {
      const next = [...prev];
      const [removed] = next.splice(from, 1);
      next.splice(to, 0, removed);
      return next;
    });
  };

  /** Mueve el bloque en fromIndex a la posición toPosition (1-based). Al pulsar "10" el bloque queda en la posición 10. */
  const handleMoveBlock = (fromIndex, toPosition) => {
    if (fromIndex === toPosition - 1) return;
    setBloques((prev) => {
      const removed = prev[fromIndex];
      const without = prev.filter((_, i) => i !== fromIndex);
      const insertAt = Math.min(Math.max(0, toPosition - 1), without.length);
      return [...without.slice(0, insertAt), removed, ...without.slice(insertAt)];
    });
  };

  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const payload = {
        titulo: titulo.trim(),
        slug: slug.trim(),
        imagen_cabecera: imagenCabecera,
        imagen_fondo: imagenFondo,
        emoji: emoji.trim(),
        bloques,
        notificacion_emails: notificacionEmails,
        notificacion_identificador_bloque_id: notificacionIdentificadorBloqueId,
      };
      await updateFormulario(id, payload);
      setFormulario((p) => (
        p
          ? {
            ...p,
            ...payload,
            slug: slug.trim(),
            notificacion_emails: notificacionEmails,
            notificacion_identificador_bloque_id: notificacionIdentificadorBloqueId,
          }
          : null
      ));
      setModoVisualizar(true);
      toast.success("Guardado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading || !user || user.permissions?.formularios === false) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  if (loading || !formulario) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className={isEmbeddedInFormularios ? "min-h-dvh" : "min-h-dvh bg-background"}>
      {!isEmbeddedInFormularios && (
        <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur">
          <div className="w-full px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate("/formularios")} className="gap-2">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                Volver
              </Button>
              <h1 className="text-lg font-semibold flex items-center gap-2">
                <FileQuestion className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} aria-hidden />
                {formulario.titulo || "Editar formulario"}
              </h1>
            </div>
            {formulario.fecha_creacion && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">
                Creada: {new Date(formulario.fecha_creacion).toLocaleString()}
              </span>
            )}
          </div>
        </header>
      )}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <TabsList className="rounded-md border border-input bg-background p-0.5 h-9">
              <TabsTrigger value="formulario" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">Formulario</TabsTrigger>
              <TabsTrigger value="respuestas" className="rounded-[5px] text-muted-foreground/50 hover:text-muted-foreground data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm flex items-center gap-2">
                Respuestas
                {(unreadByFormulario[id] ?? 0) > 0 && (
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-xs font-medium shrink-0">
                    {unreadByFormulario[id]}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            {(activeTab === "formulario" || toolbarExiting) && (
            <div
              className={`flex items-center gap-1 flex-wrap ${toolbarExiting ? "toolbar-exit" : "toolbar-enter"}`}
            >
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="bg-background border border-input text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={() => setShowDeleteFormularioConfirm(true)}
                    aria-label="Eliminar formulario"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Eliminar formulario</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="bg-background border border-input"
                    onClick={() => {
                      const url = `${window.location.origin}/f/${formulario.slug || formulario.id}`;
                      navigator.clipboard.writeText(url).then(
                        () => toast.success("Enlace copiado"),
                        () => toast.error("No se pudo copiar")
                      );
                    }}
                    aria-label="Compartir enlace"
                  >
                    <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Compartir enlace</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="bg-background border border-input"
                    onClick={handleDuplicateFormulario}
                    disabled={duplicating}
                    aria-label="Duplicar formulario"
                  >
                    {duplicating ? (
                      <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    ) : (
                      <Copy className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Duplicar formulario</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={modoVisualizar ? "secondary" : "secondary"}
                    size="icon"
                    className={modoVisualizar ? "bg-black text-white hover:bg-black/90 hover:text-white border border-black" : "bg-background border border-input"}
                    onClick={() => setModoVisualizar(true)}
                    aria-label="Visualizar"
                    aria-pressed={modoVisualizar}
                  >
                    <Eye className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Visualizar</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className={!modoVisualizar ? "bg-black text-white hover:bg-black/90 hover:text-white border border-black" : "bg-background border border-input"}
                    onClick={() => setModoVisualizar(false)}
                    aria-label="Editar"
                    aria-pressed={!modoVisualizar}
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Editar</TooltipContent>
              </Tooltip>
              <Button variant="secondary" onClick={handleSave} disabled={saving} size="sm" className="h-9 gap-1.5 ml-1 bg-background border border-input">
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                ) : (
                  <Save className="w-4 h-4" strokeWidth={1.5} aria-hidden />
                )}
                Guardar
              </Button>
            </div>
            )}
          </div>

          <TabsContent value="formulario" className="mt-6">
            <div className="rounded-2xl border border-gray-300 bg-white px-4 sm:px-6 py-6">
            {modoVisualizar ? (
              <>
                {(imagenCabecera || emoji || EMOJI_PREDETERMINADO) && (
                  <>
                    <div className="relative z-0 w-full h-[220px] rounded-lg border overflow-hidden bg-muted/30">
                      {imagenCabecera && (
                        <img src={imagenCabecera} alt="" className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="relative z-10 -mt-10 mb-10 pl-4 w-fit">
                      <span
                        className="text-7xl leading-none select-none inline-block"
                        style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                        aria-hidden
                      >
                        {emoji || EMOJI_PREDETERMINADO}
                      </span>
                    </div>
                  </>
                )}
                <h1 className="text-4xl font-semibold tracking-tight mb-8">{titulo || "Sin título"}</h1>
                <div className="space-y-4">
                  {bloques.map((block) => (
                    <BlockPreview
                      key={block.id}
                      block={block}
                      onCarouselImageClick={(urls, index) => { setLightboxUrls(urls); setLightboxIndex(index); }}
                    />
                  ))}
                </div>
                <Dialog open={lightboxUrls.length > 0} onOpenChange={(open) => !open && setLightboxUrls([])}>
                  <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto p-0 overflow-hidden flex flex-col items-center">
                    {lightboxUrls[lightboxIndex] && (
                      <img src={lightboxUrls[lightboxIndex]} alt="" className="w-full h-auto max-h-[85vh] object-contain" />
                    )}
                    {lightboxUrls.length > 1 && (
                      <div className="flex items-center justify-between w-full px-4 py-2 border-t bg-background">
                        <Button type="button" variant="outline" size="icon" onClick={() => setLightboxIndex((i) => (i <= 0 ? lightboxUrls.length - 1 : i - 1))} aria-label="Anterior">
                          <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
                        </Button>
                        <span className="text-sm text-muted-foreground">{lightboxIndex + 1} / {lightboxUrls.length}</span>
                        <Button type="button" variant="outline" size="icon" onClick={() => setLightboxIndex((i) => (i >= lightboxUrls.length - 1 ? 0 : i + 1))} aria-label="Siguiente">
                          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </>
            ) : (
            <>
            <div className="space-y-8">
            <div>
              <div className="relative z-0 rounded-lg border overflow-hidden h-[220px] bg-muted/30">
                {imagenCabecera ? (
                  <img src={imagenCabecera} alt="Cabecera" className="w-full h-full object-cover" />
                ) : (
                  <div
                    className="absolute inset-0 border-0 border-dashed flex items-center justify-center text-sm text-muted-foreground cursor-pointer hover:bg-muted/50 rounded-lg m-0"
                    style={{ borderWidth: "1px" }}
                    onClick={() => cabeceraInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add("bg-muted/50"); }}
                    onDragLeave={(e) => { e.currentTarget.classList.remove("bg-muted/50"); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove("bg-muted/50");
                      const file = (e.dataTransfer.files || [])[0];
                      if (file?.type.startsWith("image/")) {
                        readFileAsDataUrl(file).then(setImagenCabecera).catch(() => {});
                      }
                    }}
                  >
                    Arrastra una imagen o haz clic
                  </div>
                )}
                <div className="absolute bottom-2 right-2 flex gap-2 z-30">
                  <Button type="button" variant="secondary" size="sm" className="shadow" onClick={() => fondoInputRef.current?.click()}>
                    Fondo
                  </Button>
                  <Button type="button" variant="secondary" size="sm" className="shadow" onClick={() => cabeceraInputRef.current?.click()}>
                    Cambiar imagen
                  </Button>
                  <Button type="button" variant="secondary" size="sm" className="shadow" onClick={() => { setImagenCabecera(""); setImagenFondo(""); }}>Quitar todo</Button>
                </div>
              </div>
              <div className="relative z-10 -mt-10 mb-10 pl-4 w-fit pointer-events-none">
                <Popover open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
                  <PopoverTrigger asChild>
                    <span
                      className="text-7xl leading-none select-none inline-block cursor-pointer pointer-events-auto"
                      style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setEmojiPickerOpen((v) => !v); } }}
                      aria-label="Elegir emoji"
                    >
                      {emoji || EMOJI_PREDETERMINADO}
                    </span>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-0" align="start">
                    <EmojiPicker
                      onEmojiClick={(emojiData) => { setEmoji(emojiData.emoji); setEmojiPickerOpen(false); }}
                      theme="light"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <input
                ref={fondoInputRef}
                id="formulario-fondo-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = (e.target.files || [])[0];
                  if (file) readFileAsDataUrl(file).then(setImagenFondo).catch(() => {});
                  e.target.value = "";
                }}
              />
              <input
                ref={cabeceraInputRef}
                id="formulario-cabecera-file"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = (e.target.files || [])[0];
                  if (file) readFileAsDataUrl(file).then(setImagenCabecera).catch(() => {});
                  e.target.value = "";
                }}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="formulario-titulo">Título</Label>
              <Input
                id="formulario-titulo"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Título del formulario"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="formulario-slug">Enlace público (slug)</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">/f/</span>
                <Input
                  id="formulario-slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="mi-formulario"
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground">Solo letras minúsculas, números y guiones. El backend lo normalizará al guardar.</p>
            </div>

            <div className="space-y-2">
              <Label>¿Quién recibe notificación de email?</Label>
              <div className="flex flex-wrap gap-2">
                {usuariosPlataforma.length === 0 ? (
                  <span className="text-sm text-muted-foreground">Cargando usuarios…</span>
                ) : (
                  usuariosPlataforma.map((u) => {
                    const email = (u.email || "").trim().toLowerCase();
                    if (!email) return null;
                    const name = (u.nombre || u.email || email).trim() || email;
                    const selected = notificacionEmails.some((e) => (e || "").trim().toLowerCase() === email);
                    return (
                      <button
                        key={email}
                        type="button"
                        onClick={() => {
                          setNotificacionEmails((prev) => {
                            const has = prev.some((e) => (e || "").trim().toLowerCase() === email);
                            if (has) return prev.filter((e) => (e || "").trim().toLowerCase() !== email);
                            return [...prev, email];
                          });
                        }}
                        className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                          selected
                            ? "border-primary bg-primary text-primary-foreground hover:bg-primary/90"
                            : "border-input bg-background hover:bg-muted"
                        }`}
                      >
                        {name}
                      </button>
                    );
                  })
                )}
              </div>
              <p className="text-xs text-muted-foreground">Recibirán un correo cuando alguien envíe una respuesta a este formulario.</p>
            </div>

            <div className="space-y-2">
              <Label>Campo identificador para notificación</Label>
              <Select
                value={notificacionIdentificadorBloqueId || "__none__"}
                onValueChange={(v) => setNotificacionIdentificadorBloqueId(v === "__none__" ? "" : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un campo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Ninguno</SelectItem>
                  {identificadorOptions.map((opt) => (
                    <SelectItem key={opt.id} value={opt.id}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Si se selecciona, el valor de ese campo se añadirá al asunto y al cuerpo del email de nueva respuesta.
              </p>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label>Bloques</Label>
                <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-2" onClick={() => setPasteOpen(true)}>
                  <ClipboardPaste className="w-4 h-4" strokeWidth={1.5} />
                  Pegar preguntas
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2">
                      <Plus className="w-4 h-4" strokeWidth={1.5} />
                      Añadir bloque
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {BLOCK_TYPES.map((opt) => (
                      <DropdownMenuItem
                        key={opt.value}
                        onSelect={() => handleAddBlock(opt.value)}
                      >
                        {opt.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
                </div>
                <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
                  <DialogContent className="max-w-2xl">
                    <DialogHeader>
                      <DialogTitle>Pegar preguntas</DialogTitle>
                      <DialogDescription>
                        Pega el formulario en texto (#Pregunta simple - …) o JSON. Se añadirá al final de los bloques actuales.
                      </DialogDescription>
                    </DialogHeader>
                    <Textarea
                      value={pasteText}
                      onChange={(e) => setPasteText(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder={"#Pregunta simple - ¿Cuándo nació la marca?\n#Checkbox - Elige varias palabras\n- Innovación\n- Cercanía"}
                    />
                    {pasteText.trim() && (
                      <div className="text-sm space-y-1">
                        <p className="text-muted-foreground">{pasteResult.bloques.length} bloques detectados</p>
                        {pasteResult.errores.slice(0, 5).map((er, k) => (
                          <p key={k} className="text-destructive">{er}</p>
                        ))}
                      </div>
                    )}
                    <DialogFooter className="gap-2 sm:justify-between">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          navigator.clipboard?.writeText(FORM_QUESTIONS_AI_PROMPT);
                          toast.success("Instrucciones para la IA copiadas");
                        }}
                      >
                        <Copy className="w-4 h-4 mr-1" /> Copiar formato para la IA
                      </Button>
                      <Button type="button" onClick={handleImportPaste} disabled={!pasteResult.bloques.length}>
                        Añadir {pasteResult.bloques.length || ""} bloques
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="space-y-2">
                {bloques.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-4">
                    Añade bloques con el selector anterior. Usa el icono de barras para reordenar.
                  </p>
                ) : (
                  bloques.map((block, index) => (
                    <BlockRow
                      key={block.id}
                      block={block}
                      index={index}
                      allBlocks={bloques}
                      onUpdate={handleUpdateBlock}
                      onRemove={handleRemoveBlock}
                      onDuplicate={handleDuplicateBlock}
                      onMove={handleMoveBlock}
                      onDragStart={handleDragStart}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                      isDragging={dragIndex === index}
                      addWidgetMenuOptions={BLOCK_TYPES}
                      onAddWidgetAfter={(tipo) => handleAddBlockAfter(index, tipo)}
                    />
                  ))
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full gap-2 bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground py-3"
                  >
                    <Plus className="w-4 h-4" strokeWidth={1.5} />
                    + Añadir
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center">
                  {BLOCK_TYPES.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value}
                      onSelect={() => handleAddBlock(opt.value)}
                    >
                      {opt.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            </div>
            </>
            )}
            </div>
          </TabsContent>

          <TabsContent value="respuestas" className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Respuestas enviadas por candidatos en el enlace público.
            </p>
            {respuestasLoading && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
              </div>
            )}
            {respuestas.length > 0 && (
              <div className="space-y-2">
                {respuestas.map((r) => {
                  const isExpanded = expandedRespuestaId === r.id;
                  const nombre = getNombreFromRespuestas(r.respuestas);
                  const fechaStr = r.fecha_creacion ? formatRespuestaFecha(r.fecha_creacion) : r.id;
                  const isUnread = !isRespuestaRead(id, r.id);
                  return (
                    <Card key={r.id} className="shadow-none">
                      <button
                        type="button"
                        onClick={() => {
                          setExpandedRespuestaId(isExpanded ? null : r.id);
                        }}
                        className="w-full text-left py-3 px-4 flex items-center justify-between gap-2 hover:bg-muted/50 transition-colors rounded-lg"
                        aria-expanded={isExpanded}
                      >
                        <span className="text-sm font-medium truncate min-w-0 flex items-center gap-2 text-left flex-1">
                          {isUnread && (
                            <span className="inline-flex h-2 w-2 rounded-full bg-destructive shrink-0" aria-label="No leída" />
                          )}
                          <span className="truncate">{nombre}</span>
                        </span>
                        <span className="flex items-center gap-2 shrink-0 ml-2">
                          <span className="text-xs text-muted-foreground">{fechaStr}</span>
                          <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} strokeWidth={1.5} />
                        </span>
                      </button>
                      {isExpanded && (
                        <CardContent className="py-0 pb-3 px-4 pt-0">
                          <div className="space-y-3 text-sm border-t pt-3">
                            {r.respuestas && typeof r.respuestas === "object" && Object.entries(r.respuestas).map(([blockId, value]) => {
                              const titulo = blockIdToTitle[blockId] || blockId;
                              return (
                                <div key={blockId} className="flex flex-col gap-1">
                                  <dt className="text-muted-foreground text-xs font-medium">{titulo}</dt>
                                  <dd className="break-words">
                                    {blockIdToType[blockId] === "archivo" ? (
                                      <RespuestaArchivos value={value} />
                                    ) : blockIdToType[blockId] === "consentimiento_privacidad" ? (
                                      <span className={value ? "text-foreground" : "text-muted-foreground"}>
                                        {value ? "Aceptada" : "No aceptada"}
                                      </span>
                                    ) : (
                                      <RespuestaConUrls value={value} />
                                    )}
                                  </dd>
                                </div>
                              );
                            })}
                            {(!r.respuestas || Object.keys(r.respuestas).length === 0) && (
                              <p className="text-muted-foreground">Sin respuestas</p>
                            )}
                            <div className="pt-2 border-t mt-2 flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                {isUnread && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setRespuestaRead(id, r.id);
                                    }}
                                  >
                                    Marcar como leída
                                  </Button>
                                )}
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="gap-1"
                                  onClick={(e) => handleShareRespuesta(e, r.id)}
                                  disabled={sharingRespuestaId === r.id}
                                >
                                  <Share2 className="w-3 h-3" />
                                  {sharingRespuestaId === r.id ? "Copiando..." : "Compartir respuesta"}
                                </Button>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="gap-1 text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0"
                                onClick={(e) => handleDeleteRespuesta(e, r.id)}
                                title="Eliminar respuesta"
                              >
                                <Trash2 className="w-3 h-3" />
                                Eliminar
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
            {!respuestasLoading && respuestas.length === 0 && (
              <p className="text-sm text-muted-foreground">No hay respuestas recibidas.</p>
            )}
          </TabsContent>
        </Tabs>
      </main>

      <Dialog open={showDeleteFormularioConfirm} onOpenChange={setShowDeleteFormularioConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar formulario</DialogTitle>
            <DialogDescription>
              ¿Seguro que quieres eliminar este formulario? Se borrarán también todas las respuestas recibidas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowDeleteFormularioConfirm(false)} disabled={deletingFormulario}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={async () => {
                if (!id) return;
                setDeletingFormulario(true);
                try {
                  await deleteFormulario(id);
                  removeFormularioFromSidebarList?.(id);
                  toast.success("Formulario eliminado");
                  setShowDeleteFormularioConfirm(false);
                  navigate("/formularios");
                } catch (err) {
                  toast.error(err.response?.data?.detail || "Error al eliminar");
                } finally {
                  setDeletingFormulario(false);
                }
              }}
              disabled={deletingFormulario}
            >
              {deletingFormulario ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
